import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import useSWRInfinite from "swr/infinite";

import { useEffect, useMemo, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import InfiniteScroll from "react-swr-infinite-scroll";

import getUnixTime from "date-fns/getUnixTime";
import isFuture from "date-fns/isFuture";
import parseJSON from "date-fns/parseJSON";
import startOfDay from "date-fns/startOfDay";
import truncate from "lodash/truncate";
import { twMerge } from "tailwind-merge";

import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";

import AnnoucementSnippet from "components/AnnouncementSnippet";
import { Loader } from "components/customElements/loader";
import EmptyState from "components/EmptyState";
import { entityUrl } from "components/EntityPageLayout";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import { useApi } from "hooks/useApi";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import shortSecurity from "utils/shortSecurity";

import styles from "./style.module.scss";

const EventModal = dynamic(() => import("components/EventModal"));

type EntityPageEventsListProps = {};

const fetcher = async (url: string) =>
  baseFetcher(url + `&current-time=${getUnixTime(startOfDay(new Date()))}`);

export const entityEventsUrl = (
  slug: string,
  pageIndex: number,
  filterTxt?: string
) => {
  const url = new ApiEndpoint(`/api/v2/entities/${slug}/corporate-events`);
  url.searchParams.append("include", "entity");
  url.searchParams.append("page[size]", "5");
  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append("sort", "future-and-past");

  if (filterTxt) {
    url.searchParams.append("filter[fulltext]", filterTxt);
  }

  return url.href;
};

const EntityPageEventsList = ({}: EntityPageEventsListProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;
  const itemId = router.query.item as string;

  const latestEvent = useRef<any>(null);

  const [filterTxt, setFilterTxt] = useState("");
  const [filterTxtValue, setFilterTxtValue] = useState("");
  const [filterTxtError, setFilterTxtError] = useState(
    "Please enter at least 3 characters"
  );
  useEffect(() => {
    if (filterTxtValue?.length >= 3) {
      setFilterTxt(filterTxtValue);
      setFilterTxtError("");
    } else {
      setFilterTxt("");
      setFilterTxtError("Please enter at least 3 characters");
    }
  }, [filterTxtValue]);

  const { data, mutate, isValidating, size, setSize } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.data?.length === 0) return null;

      return entityEventsUrl(slug, pageIndex + 1, filterTxt);
    },
    fetcher,
    {
      revalidateFirstPage: false,
      revalidateOnMount: false,
      revalidateOnFocus: false,
    }
  );

  const hasMore = data?.[size - 1]?.meta?.totalPages > size;
  const isEmpty = !data?.[0]?.data?.length;

  useEffect(() => {
    if (!hasMore) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstEvent = data?.[0]?.data?.[0];
  const { data: entity } = useApi(entityUrl(slug));
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);
  const pageTitleName = useMemo(() => {
    if (shortedSecurity) {
      return `${name} | ${shortedSecurity} `;
    }
    return name;
  }, [shortedSecurity, name]);
  const title = `${pageTitleName} - Announcements - Events`;
  const description = truncate(
    `Latest Events on ${name} (${entity?.uniqueTicker})${
      firstEvent ? ` - ${firstEvent.title}` : ""
    }`,
    {
      length: 155,
      separator: /[/\- ]/,
    }
  );

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <div className={styles.container}>
        <div
          className={twMerge(
            styles.filter,
            "px-[15px] pb-[15px] md:px-[30px] md:py-[15px] md:flex md:items-center"
          )}
        >
          <span
            className={twMerge(styles.refresh, "hidden md:block")}
            onClick={() => mutate()}
          >
            Refresh
          </span>
          <div
            className={twMerge(
              styles.filterBox,
              "ml-0 md:ml-[30px] py-[5px] mr-[10px] w-full"
            )}
          >
            <FontAwesomeIcon icon={faSearch} />
            <OverlayTrigger
              placement="bottom"
              overlay={
                filterTxtError ? <Tooltip>{filterTxtError}</Tooltip> : <></>
              }
            >
              <input
                value={filterTxtValue}
                autoComplete="false"
                autoCapitalize="false"
                onChange={(e) => setFilterTxtValue(e.target.value)}
                aria-label="search"
                placeholder="Search"
                className="w-full md:max-w-[240px]"
              />
            </OverlayTrigger>
          </div>
        </div>
        <div className={styles.list}>
          <InfiniteScroll
            swr={{ data, mutate, isValidating, size, setSize }}
            isReachingEnd={!hasMore}
            endingIndicator={
              isValidating ? (
                <Loader numberOfItems={5} />
              ) : isEmpty ? (
                <EmptyState
                  type={filterTxt ? "search" : "event"}
                  query={filterTxt}
                  onClearClick={() => setFilterTxtValue("")}
                  entity={entity}
                />
              ) : (
                <div className={styles.endOfList}>No more events</div>
              )
            }
          >
            {(pageData: any) => {
              if (!pageData) return null;

              return pageData?.data?.map?.((event: any) => {
                let isLatest = false;
                if (
                  !latestEvent.current ||
                  latestEvent.current.id === event.id
                ) {
                  if (
                    !isFuture(parseJSON(event.startAt)) &&
                    (event.source !== "quartr" || event.sourceUrl)
                  ) {
                    isLatest = true;
                    latestEvent.current = event;
                  }
                }

                return (
                  <AnnoucementSnippet
                    key={event.id}
                    item={event}
                    isLatest={isLatest}
                  />
                );
              });
            }}
          </InfiniteScroll>
        </div>
      </div>
      {itemId && <EventModal id={itemId} />}
    </>
  );
};

export default EntityPageEventsList;
